import React, { useEffect, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import { Download, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/Components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/Components/ui/dialog';
import { getApiUrl } from '../../../config/api';

// ─────────────────────────────────────────────────────────────────────────────
// Resume access goes ONLY through the authenticated backend endpoint
// /api/resume/file, never the raw S3 link. The raw link is a permanent public
// URL, so opening/linking it (or passing it to a third-party viewer) leaks the
// candidate's resume to anyone it is shared with. The backend checks the
// viewer may access the job the resume belongs to.
// ─────────────────────────────────────────────────────────────────────────────

// File type from the URL's path (ignores any ?query or #fragment).
export function resumeKind(url) {
  const path = String(url || '').split(/[?#]/)[0].toLowerCase();
  if (path.endsWith('.pdf')) return 'pdf';
  if (path.endsWith('.docx')) return 'docx';
  if (path.endsWith('.doc')) return 'doc';
  return 'other';
}

const ERROR_MESSAGES = {
  401: 'Please sign in again to view this resume.',
  403: "You don't have access to this resume.",
  404: 'The resume file could not be found.',
  413: 'This resume is too large to preview.',
};

// Fetch the resume bytes through the backend. Throws an Error whose message is
// user-friendly and whose .status is the HTTP status.
export async function fetchResumeBlob(url) {
  const response = await fetch(getApiUrl(`/api/resume/file?url=${encodeURIComponent(url)}`), { credentials: 'include' });
  if (!response.ok) {
    let message = ERROR_MESSAGES[response.status];
    if (!message) {
      try { message = (await response.json())?.error; } catch (_) { /* not JSON */ }
    }
    const error = new Error(message || 'Could not load the resume.');
    error.status = response.status;
    throw error;
  }
  return response.blob();
}

function fileNameFor(url, candidateName) {
  const original = String(url || '').split(/[?#]/)[0].split('/').pop() || '';
  const ext = original.includes('.') ? original.split('.').pop().toLowerCase() : 'pdf';
  const base = String(candidateName || '').trim().replace(/[^\w.-]+/g, '_').replace(/^_+|_+$/g, '');
  return base ? `${base}_Resume.${ext}` : (decodeURIComponent(original) || `Resume.${ext}`);
}

// Download the resume via the backend (no raw S3 link involved). Resolves to
// true on success; on failure shows a toast and resolves to false.
export async function downloadResume(url, candidateName) {
  if (!url) { toast.error('No resume available.'); return false; }
  try {
    const blob = await fetchResumeBlob(url);
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileNameFor(url, candidateName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    return true;
  } catch (err) {
    toast.error(err.message || 'Could not download the resume.');
    return false;
  }
}

function Fallback({ url, candidateName, title, message }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <FileText className="h-8 w-8 text-muted-foreground" />
      </div>
      <div>
        <p className="text-lg font-semibold text-foreground">{title}</p>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{message}</p>
      </div>
      {url && (
        <Button onClick={() => downloadResume(url, candidateName)}>
          <Download className="mr-2 h-4 w-4" />Download Resume
        </Button>
      )}
    </div>
  );
}

function Loading({ label }) {
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-slate-100">
      <Loader2 className="h-10 w-10 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

// PDF: bytes fetched through the backend and shown from a local blob: URL, so
// the S3 link never appears in the page.
function PdfPreview({ url, candidateName }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let created = null;
    setObjectUrl(null);
    setError(null);
    fetchResumeBlob(url)
      .then((blob) => {
        if (cancelled) return;
        created = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setObjectUrl(created);
      })
      .catch((err) => { if (!cancelled) setError(err); });
    return () => { cancelled = true; if (created) URL.revokeObjectURL(created); };
  }, [url]);

  if (error) {
    return <Fallback url={error.status === 403 || error.status === 401 ? null : url} candidateName={candidateName} title="Preview unavailable" message={error.message} />;
  }
  return (
    <div className="relative h-full">
      {!objectUrl && <Loading label="Loading resume…" />}
      {objectUrl && <iframe src={`${objectUrl}#toolbar=0&navpanes=0`} className="h-full w-full border-0" title="Resume Preview" />}
    </div>
  );
}

// Word (.docx): rendered in-page by docx-preview, scaled to fit the panel.
function DocxPreview({ url, candidateName }) {
  const scrollRef = useRef(null);
  const bodyRef = useRef(null);
  const [state, setState] = useState('loading'); // loading | ready | error
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const body = bodyRef.current;
    (async () => {
      setState('loading');
      setError(null);
      try {
        const blob = await fetchResumeBlob(url);
        if (cancelled || !body) return;
        body.innerHTML = '';
        await renderAsync(blob, body, undefined, {
          className: 'docx',
          inWrapper: true,
          breakPages: true,
          useBase64URL: true, // embedded images as data URLs (no blob: leaks)
        });
        if (cancelled) return;
        const wrapper = body.querySelector('.docx-wrapper');
        const page = body.querySelector('section.docx');
        const available = (scrollRef.current?.clientWidth || 0) - 8;
        if (wrapper && page && available > 0 && page.offsetWidth > available) {
          wrapper.style.zoom = String(available / (page.offsetWidth + 32));
        }
        setState('ready');
      } catch (err) {
        console.error('Could not render .docx resume:', err);
        if (!cancelled) { setError(err); setState('error'); }
      }
    })();
    return () => { cancelled = true; if (body) body.innerHTML = ''; };
  }, [url]);

  if (state === 'error') {
    const denied = error?.status === 403 || error?.status === 401;
    return <Fallback url={denied ? null : url} candidateName={candidateName} title="Preview unavailable" message={error?.message || "This Word resume couldn't be displayed here."} />;
  }
  return (
    <div ref={scrollRef} className="relative h-full overflow-auto bg-slate-100">
      {state === 'loading' && <Loading label="Rendering Word resume…" />}
      <style>{'.resume-docx .docx-wrapper{background:transparent;padding:16px 0}.resume-docx .docx-wrapper>section.docx{box-shadow:0 1px 3px rgba(15,23,42,.12);margin-bottom:16px}'}</style>
      <div ref={bodyRef} className="resume-docx" />
    </div>
  );
}

// Picks the right renderer for the resume file type.
export default function ResumePreview({ url, candidateName }) {
  const kind = resumeKind(url);
  if (kind === 'pdf') return <PdfPreview url={url} candidateName={candidateName} />;
  if (kind === 'docx') return <DocxPreview url={url} candidateName={candidateName} />;
  if (kind === 'doc') {
    return (
      <Fallback
        url={url}
        candidateName={candidateName}
        title="Older Word format (.doc)"
        message="Browsers can't preview legacy .doc files. Download it to view, or ask the candidate for a PDF or .docx."
      />
    );
  }
  return <Fallback url={url} candidateName={candidateName} title="Preview unavailable" message="This resume format can't be previewed here." />;
}

// A ready-made dialog: in-app viewer + Download. Use instead of opening the
// raw resume link in a new tab.
export function ResumeViewerDialog({ url, candidateName, onClose }) {
  return (
    <Dialog open={Boolean(url)} onOpenChange={(open) => { if (!open) onClose(); }}>
      {/* z-[1400]: opens above the admin page's custom modals (up to z-[1300]) */}
      <DialogContent overlayClassName="z-[1400]" className="z-[1400] flex h-[88vh] max-w-4xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="flex-row items-center justify-between space-y-0 border-b px-5 py-3 pr-12">
          <div className="min-w-0">
            <DialogTitle className="truncate text-base">{candidateName ? `${candidateName} — Resume` : 'Resume'}</DialogTitle>
            <DialogDescription className="sr-only">Candidate resume preview</DialogDescription>
          </div>
          <Button size="sm" variant="outline" onClick={() => downloadResume(url, candidateName)}>
            <Download className="mr-1.5 h-4 w-4" />Download
          </Button>
        </DialogHeader>
        <div className="min-h-0 flex-1">
          {url && <ResumePreview url={url} candidateName={candidateName} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
